import styled, { css } from 'styled-components';

import { SPACING } from '~/common/layout-propertis/spacing';
import { RADIUS } from '~/common/layout-propertis/radius';
import { Z_INDEX } from '~/common/constants/z-index';

import {
  TBorderRadius,
  TDropdownPosition,
  TDropdownSize,
  TDropdownVariant,
} from '../types';
import { getDropdownHeight, getDropdownPadding } from '../styles';

interface IOptionProps {
  $size: TDropdownSize;
  $isDisabled?: boolean;
  $variant: TDropdownVariant;
  $isSelected: boolean;
}

interface INoOptionsProps {
  $size: TDropdownSize;
}

interface IContainerProps {
  $isVisible: boolean;
  $containerClientRect: DOMRect;
  $position: TDropdownPosition;
}

interface IListWrapperProps {
  $isVisible: boolean;
  $inputWrapperClientRect: DOMRect;
  $position: TDropdownPosition;
}

interface IListContentProps {
  $isVisible: boolean;
  $position: TDropdownPosition | TBorderRadius;
}

export const Container = styled.div<IContainerProps>`
  position: absolute;
  height: ${({ $containerClientRect }) => $containerClientRect?.height || 0}px;
  width: ${({ $containerClientRect }) => $containerClientRect?.width || 0}px;
  left: ${({ $containerClientRect }) => $containerClientRect?.left || 0}px;
  top: ${({ $containerClientRect }) => $containerClientRect?.top || 0}px;

  transition: visibility ease-in 0.2s;

  visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};
`;

export const ListWrapper = styled.div<IListWrapperProps>`
  position: absolute;
  height: ${({ $inputWrapperClientRect }) =>
    $inputWrapperClientRect?.height || 0}px;
  width: ${({ $inputWrapperClientRect }) =>
    $inputWrapperClientRect?.width || 0}px;
  left: ${({ $inputWrapperClientRect }) =>
    $inputWrapperClientRect?.left || 0}px;
  top: ${({ $inputWrapperClientRect }) => $inputWrapperClientRect?.top || 0}px;

  transition: visibility ease-in 0.2s;

  visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};
`;

export const ListContent = styled.div<IListContentProps>`
  position: absolute;
  background-color: ${({ theme }) => theme.neutral[950]};
  border: ${({ theme }) => `1px solid ${theme.neutral[700]}`};

  width: 100%;

  box-shadow: 0px 16px 32px -12px #0e121b1a;

  overflow: hidden;
  z-index: ${Z_INDEX.DROPDOWN};

  transition: all ease-out 0.2s;

  opacity: 0;
  visibility: hidden;

  ${({ $position }) =>
    $position === 'bottom'
      ? css`
          border-bottom-left-radius: ${RADIUS._16};
          border-bottom-right-radius: ${RADIUS._16};
          top: 100%;
        `
      : $position === 'full'
      ? css`
          border-bottom-left-radius: ${RADIUS._16};
          border-bottom-right-radius: ${RADIUS._16};
          border-top-left-radius: ${RADIUS._16};
          border-top-right-radius: ${RADIUS._16};
          top: 100%;
        `
      : css`
          border-top-left-radius: ${RADIUS._16};
          border-top-right-radius: ${RADIUS._16};
          bottom: 100%;
        `}

  ${({ $isVisible }) =>
    $isVisible &&
    css`
      opacity: 1;
      visibility: visible;
    `}

  > ul {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-height: 200px;
    overflow-y: auto;
    overflow-x: hidden;
    padding: ${SPACING._8} 0;
    gap: ${SPACING._4};
  }
`;

export const Option = styled.li<IOptionProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
  min-height: ${({ $variant, $size }) =>
    $variant === 'compact' ? getDropdownHeight[$size] : '56px'};
  padding: ${({ $variant, $size }) =>
    $variant === 'compact'
      ? getDropdownPadding[$size]
      : `${SPACING._8} ${SPACING._16}`};
  gap: ${SPACING._8};
  background-color: ${({ theme }) => theme.neutral[950]};

  cursor: pointer;

  transition: all ease-out 0.2s;

  ${({ $isSelected }) =>
    $isSelected &&
    css`
      background-color: ${({ theme }) => theme.neutral[900]};
    `}

  ${({ $isDisabled }) =>
    $isDisabled
      ? css`
          cursor: not-allowed;
          opacity: 0.48;
        `
      : css`
          &:hover {
            background-color: ${({ theme }) => theme.neutral[900]};
          }
        `}
`;

export const OptionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING._4};

  flex: 1;

  > header {
    display: flex;
    align-items: center;
    gap: ${SPACING._4};
  }
`;

export const OptionLabelSublabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING._4};
  margin-right: auto;
`;

export const NoOptionsWrapper = styled.span<INoOptionsProps>`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: ${({ $size }) => getDropdownHeight[$size]};
  text-align: center;
`;
